import { TextField } from '@mui/material';
import { Field, useFormikContext } from 'formik';
import { camelCase } from 'lodash';
import React, { ChangeEvent, useCallback, useMemo } from 'react';

import { InputComponent } from '..';
import { normalizeSize } from '../helpers';

export interface InputFileUploadProps extends Omit<InputComponent, 'size'> {
  size?: InputComponent['size'] | 'small' | 'medium' | 'large';
  onChange?: (name: string, value?: File) => void;
  accept?: string;
  multiple?: boolean;
}

export function InputFileUpload({
  label,
  disabled,
  hidden,
  required,
  className,
  hint,
  nameOverride,
  placeholder,
  size: propSize = 'medium',
  onChange = undefined,
  showLabel = !hidden,
  readOnly = false,
  accept = undefined,
  multiple = false,
}: InputFileUploadProps) {
  const { errors, setFieldValue } = useFormikContext<Record<string, string>>();

  const validate = useCallback(
    (value: File) => {
      if (required && !value) {
        return `${label} is required.`;
      }
      return undefined;
    },
    [label, required],
  );

  const name = useMemo(
    () => nameOverride ?? camelCase(label),
    [label, nameOverride],
  );

  const inputPlaceholder = useMemo(
    () => placeholder ?? label?.replace(/[?:]/g, ''),
    [placeholder, label],
  );

  const size = normalizeSize(propSize);

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = multiple
        ? Array.from(event.target.files || [])
        : event.target.files?.[0];

      onChange?.(name, file as File);
      setFieldValue(name, file);
    },
    [name, onChange, setFieldValue, multiple],
  );

  return (
    <Field name={name} validate={validate}>
      {() => (
        <TextField
          name={name}
          fullWidth
          variant="outlined"
          label={showLabel && label}
          className={className}
          disabled={disabled}
          hidden={hidden}
          required={required && !readOnly}
          placeholder={inputPlaceholder}
          size={size === 'large' ? 'medium' : size}
          type="file"
          error={!!errors[name]}
          helperText={errors[name] ? errors[name] : hint}
          slotProps={{
            inputLabel: {
              shrink: true, // Always shrink for file inputs
            },
            input: {
              inputProps: {
                accept,
                multiple,
                readOnly,
              } as React.HTMLAttributes<HTMLInputElement>,
            },
          }}
          onChange={handleFileChange}
          // File inputs don't have a controlled value
          value=""
        />
      )}
    </Field>
  );
}
